<template>
    <div :class="snackbarPortalMergedClasses">
        <transition-group
            tag="span"
            class="flex flex-col items-start"
            :class="{ '--empty': activeSnackbars?.length === 0 }"
            @click="handlePortalClick"
            @before-enter="beforeEnterHook"
            @enter="enterHook"
            @after-enter="afterEnterHook"
            @before-leave="beforeLeaveHook"
            @leave="leaveHook"
        >
            <div v-for="snackbar in activeSnackbars" :key="snackbar.id" class="relative mt-2 max-w-full">
                <SnackbarElement
                    data-type="snackbar"
                    :id="snackbar.id"
                    :title="snackbar.title"
                    :text="snackbar.text"
                    :subText="snackbar.subText"
                    :type="snackbar.type"
                    :duration="snackbar.duration"
                    :closable="snackbar.closable"
                    :actionFunction="snackbar.actionFunction"
                    :actionLabel="snackbar.actionLabel"
                    :pauseOnHover="snackbar.pauseOnHover"
                    @click="handleClick(snackbar)"
                    @close="closeSnackbar(snackbar.id)"
                    @action="handleAction(snackbar)"
                    :key="snackbar.id"
                />
            </div>
        </transition-group>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import SnackbarElement from './SnackbarElement.vue'
    import type { SnackbarComponent } from './types'
    import { inject } from 'vue'
    import { twMerge } from 'tailwind-merge'

    const props = withDefaults(
        defineProps<{
            class?: string | string[]
        }>(),
        {
            class: undefined
        }
    )

    // Inject snackbar composable
    // Use Vue's provide/inject pattern to allow for a global snackbar composable
    const snackbar = inject('snackbar')
    if (!snackbar) {
        throw new Error('Snackbar composable not provided. See documentation for more information.')
    }
    const { closeSnackbar, handlePortalClick, handleClick, handleAction, activeSnackbars } = snackbar as {
        closeSnackbar: (id: string | number) => void
        handlePortalClick: () => void
        handleClick: (snackbar: SnackbarComponent) => void
        handleAction: (snackbar: SnackbarComponent) => void
        activeSnackbars: SnackbarComponent[]
    }

    /**
     * Transition configuration
     */
    const beforeEnterHook = (el: Element) => {
        const snackbarWrapper = el as HTMLElement
        if (!snackbarWrapper) return

        snackbarWrapper.style.height = '0'
        snackbarWrapper.style.opacity = '0'
        snackbarWrapper.style.scale = '0.8'
        snackbarWrapper.style.marginTop = '0'
        snackbarWrapper.style.transition = 'all 500ms ease, scale 700ms ease, height 200ms ease'
    }
    const enterHook = (el: Element, done: () => void) => {
        const snackbarWrapper = el as HTMLElement
        const snackbarElement = snackbarWrapper.querySelector('[data-type="snackbar"]')
        if (!snackbarWrapper || !snackbarElement) return done()

        snackbarWrapper.style.marginTop = '8px'
        snackbarWrapper.style.height = snackbarElement.scrollHeight + 'px'
        snackbarWrapper.style.opacity = '1'
        snackbarWrapper.style.scale = '1'

        // Signal latest transition end to vue transition
        el.addEventListener('transitionend', (event) => {
            const transitionEvent = event as TransitionEvent
            if (transitionEvent.propertyName !== 'scale') return
            done()
        })
    }
    const afterEnterHook = (el: Element) => {
        const snackbarWrapper = el as HTMLElement
        snackbarWrapper.removeAttribute('style')
    }
    const beforeLeaveHook = (el: Element) => {
        const snackbarWrapper = el as HTMLElement
        const snackbarElement = snackbarWrapper.querySelector('[data-type="snackbar"]')
        if (!snackbarWrapper || !snackbarElement) return
        snackbarWrapper.style.transition = 'all 300ms ease, scale 300ms ease, margin-top 300ms ease 150ms, height 300ms ease 150ms'
        snackbarWrapper.style.height = snackbarElement.scrollHeight + 'px'
        snackbarWrapper.style.opacity = '1'
        snackbarWrapper.style.scale = '1'
        snackbarWrapper.style.marginTop = '8px'
    }
    const leaveHook = (el: Element, done: () => void) => {
        const snackbarWrapper = el as HTMLElement
        if (!snackbarWrapper) return done()

        snackbarWrapper.style.height = '0'
        snackbarWrapper.style.opacity = '0'
        snackbarWrapper.style.scale = '0.8'
        snackbarWrapper.style.marginTop = '0'

        // Signal latest transition end to vue transition
        el.addEventListener('transitionend', (event) => {
            const transitionEvent = event as TransitionEvent
            if (transitionEvent.propertyName !== 'height') return
            done()
        })
    }

    const snackbarPortalBaseClasses = computed(() => {
        return 'pointer-events-none fixed z-[2000] p-2 left-0 bottom-0 max-w-full'
    })

    // Allow overriding base classes
    const snackbarPortalMergedClasses = computed(() => twMerge(snackbarPortalBaseClasses.value, props.class))
</script>
